import { supabase } from "../lib/supabase";

/**
 * Centralized role detection for the ISOGyaan platform
 *
 * Role Hierarchy:
 * 1. Platform Admin - Full platform access (ISOGyaan staff)
 * 2. Partner Admin - Partner store owner
 * 3. Trainee - Partner's customer (stored in partner_customers)
 * 4. Normal User - Regular student
 */

/**
 * Check if user is a Platform Admin (ISOGyaan staff)
 * This is the primary admin check used throughout the application.
 *
 * @param {Object} user - Supabase auth user object
 * @param {Object} userProfile - User profile from user_profiles table
 * @returns {boolean}
 */
export const isPlatformAdmin = (user, userProfile) => {
  if (!user) return false;

  return userProfile?.role === "admin";
};

/**
 * Check if an email belongs to a platform admin
 * Useful for quick checks without full user object
 *
 * @param {string} email
 * @returns {boolean}
 */
export const isPlatformAdminEmail = async (email) => {
  if (!email) return false;

  try {
    const { data, error } = await supabase
      .from("user_profiles")
      .select("id")
      .eq("email", email.toLowerCase())
      .eq("role", "admin")
      .limit(1)
      .maybeSingle();

    if (error) throw error;
    return !!data;
  } catch (error) {
    console.error("Error checking admin email:", error);
    return false;
  }
};

/**
 * Get the list of platform admin emails
 * Returns a copy to prevent modification
 *
 * @returns {string[]}
 */
export const getPlatformAdminEmails = async () => {
  try {
    const { data, error } = await supabase
      .from("user_profiles")
      .select("email")
      .eq("role", "admin");

    if (error) throw error;
    return (data || []).map((p) => p.email);
  } catch (error) {
    console.error("Error getting platform admin emails:", error);
    return [];
  }
};

/**
 * Alias for isPlatformAdmin for backwards compatibility
 * Some components use "isAdmin" naming
 *
 * @param {Object} user - Supabase auth user object
 * @param {Object} userProfile - User profile from user_profiles table
 * @returns {boolean}
 */
export const isAdmin = isPlatformAdmin;

/**
 * Get user's partner role (async)
 *
 * @param {string} userId
 * @returns {Promise<{team: Object|null, role: 'admin'|null}>}
 */
export const getPartnerRole = async (userId) => {
  if (!userId) return { team: null, role: null };

  try {
    const { teamService } = await import("../services/partnerService");
    return await teamService.getTeamForUser(userId);
  } catch (error) {
    console.error("Error getting partner role:", error);
    return { team: null, role: null };
  }
};

/**
 * Check if user is a Partner Admin (owns a partner store)
 *
 * @param {string} userId
 * @returns {Promise<boolean>}
 */
export const isPartnerAdmin = async (userId) => {
  const { role } = await getPartnerRole(userId);
  return role === "admin";
};

/**
 * Quick synchronous check if user is an Instructor based on role field.
 *
 * NOTE: For dual-role users (partner + instructor), the role field may not
 * be 'instructor'. For authoritative instructor detection, use
 * isInstructorAsync() which queries instructor_profiles.approval_status.
 *
 * @param {Object} user - Supabase auth user object
 * @param {Object} userProfile - User profile from user_profiles table
 * @returns {boolean}
 */
export const isInstructor = (user, userProfile) => {
  if (!user) return false;
  return userProfile?.role === "instructor";
};

/**
 * Authoritative instructor check via instructor_profiles.approval_status.
 * Use this wherever instructor UI gating needs to respect dual-role users
 * (admin+instructor, partner+instructor) whose user_profiles.role may be
 * 'admin' or 'student' even though they're approved instructors.
 *
 * @param {string} userId
 * @returns {Promise<boolean>}
 */
export const isInstructorAsync = async (userId) => {
  if (!userId) return false;
  try {
    const { data, error } = await supabase
      .from("instructor_profiles")
      .select("approval_status")
      .eq("id", userId)
      .maybeSingle();
    if (error) throw error;
    return data?.approval_status === "approved";
  } catch (error) {
    console.error("Error checking instructor status:", error);
    return false;
  }
};
